/* src/components/admin/CreateUserModal.tsx */
import React, { useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { adminUserService, CreateUserData } from '../../services/admin.user.service';
import { errorService } from '../../services/error.service';
import useForm, { ValidationRules } from '../../hooks/useForm';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSave, 
    faUser, 
    faEnvelope, 
    faLock, 
    faShieldAlt,
    faCheck,
    faEye,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import styles from '../../styles/components/CreateUserModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';


interface CreateUserModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    roles: { id: number; roleName: string }[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ show, onHide, onSuccess, roles }) => {
    const initialValues = React.useMemo<CreateUserData>(() => ({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        idRoles: []
    }), []);

    const validationRules = React.useMemo<ValidationRules<CreateUserData>>(() => ({
        firstName: (val) => (!val?.trim() ? 'El nombre es obligatorio' : null),
        lastName: (val) => (!val?.trim() ? 'El apellido es obligatorio' : null),
        email: (val) => {
            if (!val?.trim()) return 'El correo electrónico es obligatorio';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(val)) return 'Formato de correo inválido';
            return null;
        },
        password: (val) => {
            if (!val?.trim()) return 'La contraseña es obligatoria';
            if (val.length < 8 || val.length > 16) return 'Debe tener entre 8 y 16 caracteres';
            return null;
        },
        idRoles: (val) => (val?.length === 0 ? 'Debes seleccionar al menos un rol' : null)
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        validate,
        resetForm,
        setValues
    } = useForm<CreateUserData>(
        initialValues,
        validationRules
    );
    
    const [loading, setLoading] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);

    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show, resetForm]);

    const toggleRole = (roleId: number) => {
        const isSelected = values.idRoles.includes(roleId);
        const newRoles = isSelected
            ? values.idRoles.filter(id => id !== roleId)
            : [...values.idRoles, roleId];
            
        setValues(prev => ({ ...prev, idRoles: newRoles }));
    };

    const onSubmit = async () => {
        try {
            setLoading(true);
            const response = await adminUserService.createUser(values);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                await Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'El usuario ha sido creado correctamente.',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: 'var(--color-primary)'
                });
            } else {
                errorService.showErrorAlert(response, 'Error al Crear Usuario');
            }
        } catch (error: any) {
            console.error('Error in CreateUserModal:', error);
            errorService.showErrorAlert(error, 'Error de Conexión');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.createUserModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Crear Nuevo Usuario
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Completa la información para registrar un nuevo administrador en el sistema.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)} id="create-user-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Nombre</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    placeholder="Nombre"
                                    value={values.firstName}
                                    onChange={handleChange}
                                    className={`${styles.createUserModalInput} ${errors.firstName ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.firstName && <div className={styles.errorText}>{errors.firstName}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Apellido</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    placeholder="Apellido"
                                    value={values.lastName}
                                    onChange={handleChange}
                                    className={`${styles.createUserModalInput} ${errors.lastName ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.lastName && <div className={styles.errorText}>{errors.lastName}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Correo Electrónico</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                                <Form.Control
                                    type="email"
                                    name="email"
                                    placeholder="ejemplo@correo.com"
                                    value={values.email}
                                    onChange={handleChange}
                                    className={`${styles.createUserModalInput} ${errors.email ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                            </div>
                            {errors.email && <div className={styles.errorText}>{errors.email}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Contraseña</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="8 a 16 caracteres"
                                    value={values.password}
                                    onChange={handleChange}
                                    className={`${styles.createUserModalInput} ${errors.password ? styles.inputError : ''}`}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                            {errors.password && <div className={styles.errorText}>{errors.password}</div>}
                        </Form.Group>
                    </div>

                    <div className={styles.rolesSection}>
                        <Form.Label className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" style={{ color: "var(--brand-black)" }} />
                            Asignar Roles
                        </Form.Label>
                        <div className={styles.rolesGrid}>
                            {roles.map(role => (
                                <div 
                                    key={role.id} 
                                    className={`${styles.roleItem} ${values.idRoles.includes(role.id) ? styles.roleItemActive : ''} ${errors.idRoles ? styles.roleError : ''}`}
                                    onClick={() => toggleRole(role.id)}
                                >
                                    <div className={styles.roleCheckbox}>
                                        {values.idRoles.includes(role.id) && <FontAwesomeIcon icon={faCheck} />}
                                    </div>
                                    <span>{role.roleName}</span>
                                </div>
                            ))}
                        </div>
                        {errors.idRoles && <div className={styles.errorText}>{errors.idRoles}</div>}
                        {roles.length === 0 && (
                            <p className="text-muted small mt-2">No hay roles disponibles para asignar.</p>
                        )}
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button 
                    variant="secondary" 
                    onClick={onHide} 
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    type="submit" 
                    form="create-user-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Creando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar Usuario
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateUserModal;
